import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  ArrowLeft,
  Clock,
  Target,
  Shield,
  DollarSign,
  BarChart3,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const SignalDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [chartData, setChartData] = useState([]);

  // Use the signal data passed from navigation or fall back to mock data
  const signalData = location.state?.signal || {
    id: 1,
    symbol: "WTI Crude",
    direction: "sell",
    currentPrice: 67.437,
    priceChange: -0.184,
    priceChangePercent: -0.27,
    entry: 67.770,
    stopLoss: 69.000,
    takeProfit: 62.000,
    timeframe: "4H",
    strategy: "Support/Resistance",
    confidence: "High",
    timestamp: new Date().toISOString(),
    pnl: -15.50,
    isActive: true,
    analysis:
      "Following buying pressure at the start of the week, the pair consolidated yesterday with little net change and all price action within the upper half of the previous day's range.",
    technicalNote: "Engulfing resistance is located at 67.77",
    positionNote: "Selling posted in Asia",
    riskNote: "There is scope for mild buying at the open but gains should be limited.",
    termNote: "The medium term bias remains bearish",
  };

  // Generate mock chart data
  useEffect(() => {
    const generateChartData = () => {
      const data = [];
      const basePrice = 67.437;

      for (let i = 0; i < 50; i++) {
        const volatility = 0.5;
        const trend = -0.01; // Slight downward trend
        const noise = (Math.random() - 0.5) * volatility;
        const price = basePrice + i * trend + noise;

        data.push({
          time: i,
          price: price,
          high: price + Math.random() * 0.3,
          low: price - Math.random() * 0.3,
          volume: Math.random() * 100,
        });
      }
      return data;
    };

    setChartData(generateChartData());

    // Update time every second
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const supportResistanceLevels = [
    { type: "RESISTANCE", level: 67.770, color: "text-red-400" },
    { type: "RESISTANCE", level: 68.340, color: "text-red-400" },
    { type: "RESISTANCE", level: 69.000, color: "text-red-400" },
    { type: "SUPPORT", level: 65.970, color: "text-emerald-400" },
    { type: "SUPPORT", level: 64.470, color: "text-emerald-400" },
    { type: "SUPPORT", level: 62.000, color: "text-emerald-400" },
  ];

  const MockChart = () => (
    <div className="h-64 bg-slate-800 rounded-lg p-4 relative overflow-hidden">
      {/* Chart grid */}
      <div className="absolute inset-0 opacity-10">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute w-full border-t border-slate-600"
            style={{ top: `${i * 20}%` }}
          />
        ))}
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="absolute h-full border-l border-slate-600"
            style={{ left: `${i * 10}%` }}
          />
        ))}
      </div>

      {/* Mock candlestick chart */}
      <div className="flex items-end justify-between h-full px-4">
        {chartData.slice(0, 30).map((candle, i) => (
          <div
            key={i}
            className="flex flex-col items-center relative"
            style={{ width: "2%" }}
          >
            {/* Candlestick body */}
            <div
              className={`w-full ${
                candle.price > 67.4 ? "bg-emerald-500" : "bg-red-500"
              } opacity-80`}
              style={{
                height: `${Math.abs(candle.high - candle.low) * 40}px`,
                marginBottom: `${(candle.price - 66) * 30}px`,
              }}
            />
            {/* Volume bar */}
            <div
              className="w-full bg-blue-500 opacity-30 mt-1"
              style={{ height: `${candle.volume * 0.3}px` }}
            />
          </div>
        ))}
      </div>

      {/* Price levels overlay */}
      <div className="absolute right-0 top-0 h-full w-16 bg-gradient-to-l from-slate-800 to-transparent">
        <div className="h-full flex flex-col justify-between py-4 pr-2">
          <div className="text-xs text-emerald-400 font-mono">69.00</div>
          <div className="text-xs text-slate-400 font-mono">68.00</div>
          <div className="text-xs text-white font-mono font-bold">67.44</div>
          <div className="text-xs text-slate-400 font-mono">66.00</div>
          <div className="text-xs text-red-400 font-mono">65.00</div>
        </div>
      </div>
    </div>
  );

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleBack}
            className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 text-sm text-slate-400">
            <span>← BACK TO LIST</span>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Price Info & Chart */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price Header */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 border border-slate-700/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">C</span>
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-white">
                      {signalData.symbol}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded border border-red-500/30">
                        SELL LIMIT
                      </span>
                      <span className="text-slate-400 text-sm">
                        {signalData.timeframe}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-3xl font-bold text-white">
                    {signalData.currentPrice}
                  </div>
                  <div className="flex items-center gap-1 text-red-400">
                    <TrendingDown className="h-4 w-4" />
                    <span>
                      {signalData.priceChange} ({signalData.priceChangePercent}%)
                    </span>
                  </div>
                </div>
              </div>

              {/* Price Details */}
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-slate-400 text-xs mb-1">Entry</div>
                  <div className="text-white font-mono">{signalData.entry}</div>
                </div>
                <div>
                  <div className="text-slate-400 text-xs mb-1">Target</div>
                  <div className="text-emerald-400 font-mono">
                    {signalData.takeProfit}
                  </div>
                </div>
                <div>
                  <div className="text-slate-400 text-xs mb-1">Stop</div>
                  <div className="text-red-400 font-mono">
                    {signalData.stopLoss}
                  </div>
                </div>
                <div>
                  <div className="text-slate-400 text-xs mb-1">
                    Intraday Duration
                  </div>
                  <div className="text-white font-mono">4H</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-4">
                <button className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-lg font-medium transition-colors">
                  TRADE
                </button>
                <button className="px-6 py-3 border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 rounded-lg transition-colors">
                  News Sentiment
                </button>
                <button className="px-6 py-3 border border-slate-600 text-slate-400 hover:text-white hover:border-slate-500 rounded-lg transition-colors">
                  Confidence
                </button>
              </div>
            </div>

            {/* Chart */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 border border-slate-700/50">
              <h3 className="text-lg font-semibold text-white mb-4">
                Market Chart (4H)
              </h3>
              <MockChart />
            </div>

            {/* Trade Idea */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 border border-slate-700/50">
              <div className="flex items-center gap-3 mb-4">
                <h3 className="text-lg font-semibold text-white">Trade Idea</h3>
                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded border border-red-500/30">
                  SELL LIMIT
                </span>
              </div>

              <div className="space-y-3 text-sm">
                <div className="text-slate-300">
                  <span className="text-slate-400">Opened:</span>{" "}
                  {new Date(signalData.timestamp).toLocaleString()}
                </div>
                <div className="text-slate-300">
                  <span className="text-slate-400">Expires at:</span> 06/07/2025
                  05:00
                </div>

                <div className="bg-slate-700/50 p-4 rounded-lg mt-4">
                  <p className="text-slate-300 leading-relaxed mb-3">
                    {signalData.analysis}
84                  </p>
                    <p className="text-slate-300 mb-2">
                      <span className="text-blue-400">Engulfing resistance</span>{" "}
                      is located at {signalData.entry}
                    </p>
                    <p className="text-slate-300 mb-2">
                      <span className="text-emerald-400">Selling posted in Asia</span>
                    </p>
                    <p className="text-slate-300 mb-2">{signalData.riskNote}</p>
                    <p className="text-slate-300 font-medium">
                      {signalData.termNote}
                    </p>
                  </div>

                  <button className="w-full bg-blue-500 hover:bg-blue-600 text-white py-3 px-6 rounded-lg font-medium transition-colors mt-4">
                    TRADE
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - Support/Resistance & Stats */}
            <div className="space-y-6">
              {/* Support/Resistance Levels */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Key Levels
                </h3>
                <div className="space-y-3">
                  {supportResistanceLevels.map((level, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <span className={`text-sm font-medium ${level.color}`}>
                        {level.type} {index + 1}
                      </span>
                      <span className={`font-mono ${level.color}`}>
                        {level.level}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Signal Stats */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Signal Info
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Strategy</span>
                    <span className="text-white font-medium">
                      {signalData.strategy}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Confidence</span>
                    <span className="text-emerald-400 font-medium">
                      {signalData.confidence}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Time Frame</span>
                    <span className="text-white font-medium">
                      {signalData.timeframe}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Current P&L</span>
                    <span
                      className={`font-medium ${
                        signalData.pnl >= 0 ? "text-emerald-400" : "text-red-400"
                      }`}
                    >
                      {signalData.pnl >= 0 ? "+" : ""}
                      {signalData.pnl} pips
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400">Status</span>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                      <span className="text-emerald-400 font-medium">
                        Active
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Risk Management */}
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 border border-slate-700/50">
                <h3 className="text-lg font-semibold text-white mb-4">
                  Risk Management
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                    <Shield className="h-5 w-5 text-red-400" />
                    <div>
                      <div className="text-sm font-medium text-red-400">
                        Stop Loss
                      </div>
                      <div className="text-xs text-slate-400">
                        Risk:{" "}
                        {(
                          ((signalData.stopLoss - signalData.entry) /
                            signalData.entry) *
                          100
                        ).toFixed(2)}
                        %
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <Target className="h-5 w-5 text-emerald-400" />
                    <div>
                      <div className="text-sm font-medium text-emerald-400">
                        Take Profit
                      </div>
                      <div className="text-xs text-slate-400">
                        Reward:{" "}
                        {(
                          ((signalData.entry - signalData.takeProfit) /
                            signalData.entry) *
                          100
                        ).toFixed(2)}
                        %
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-blue-400" />
                    <div>
                      <div className="text-sm font-medium text-blue-400">
                        Risk/Reward
                      </div>
                      <div className="text-xs text-slate-400">Ratio: 1:2.5</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default SignalDetailPage;